<template>
  <div>
    <b-row
      v-if="totalItems > pageSize"
      class="mb-1 align-items-center"
    >
      <b-col>
        <span class="text-muted small">
          Showing {{ paginatedStartIndex + 1 }}–{{ Math.min(paginatedStartIndex + pageSize, totalItems) }} of {{ totalItems }} items
          <b-spinner
            v-if="isLoadingItems"
            small
            class="ml-1"
          />
          <span
            v-if="itemsWithIssuesCount > 0"
            class="ml-1 text-danger font-weight-bold"
          >
            ({{ itemsWithIssuesCount }} with errors — shown first)
          </span>
        </span>
      </b-col>
      <b-col class="text-right">
        <b-button
          :disabled="currentPage === 1 || isLoadingItems"
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          @click="serverTotalItems !== null ? fetchItemsFromServer(currentPage - 1) : currentPage--"
        >
          &laquo; Prev
        </b-button>
        <b-button
          :disabled="currentPage >= totalPages || isLoadingItems"
          size="sm"
          variant="outline-secondary"
          @click="serverTotalItems !== null ? fetchItemsFromServer(currentPage + 1) : currentPage++"
        >
          Next &raquo;
        </b-button>
      </b-col>
    </b-row>
    <b-row class="overflow-x-scroll">
      <b-overlay
        :show="isLoadingItems"
        opacity="0.6"
        spinner-variant="primary"
        class="w-100"
      >
        <b-table
          :items="paginatedItems"
          :fields="tableFields"
          small
          class="compact-headers mx-0"
        >
          <template #custom-foot>
            <b-tr
              v-for="total in pageTotalRows"
              :key="total.value_currency || 'page-total'"
              class="parcel-items-page-total table-active font-weight-bold"
            >
              <b-td
                v-for="field in tableFields"
                :key="field.key"
                :class="`parcel-items-page-total-${field.key}`"
              >
                <span
                  v-if="total[field.key]"
                  :class="['d-block', { 'pl-1': !disabled }]"
                >
                  {{ total[field.key] }}
                </span>
              </b-td>
            </b-tr>
          </template>

          <template #cell(rowNumber)="data">
            {{ paginatedStartIndex + data.index + 1 }}
          </template>

          <template #cell(description)="data">
            <div v-if="disabled">
              {{ data.item.description }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Description"
                rules="required"
              >
                <div class="d-flex align-items-center">
                  <b-form-input
                    id="description"
                    v-model="data.item.description"
                    v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'description')"
                    :class="issueClass(getUnifiedIssues(data.item), 'description')"
                    :state="errors.length > 0 ? false:null"
                    size="sm"
                    class="mr-1"
                  />

                  <feather-icon
                    v-if="data.item.description !== data.item.original_description"
                    v-b-tooltip.hover.right="data.item.original_description"
                    icon="AlertCircleIcon"
                    class="d-inline"
                    size="1.5x"
                  />

                </div>

              </validation-provider>
            </div>
          </template>

          <template #cell(hs_code)="data">
            <div v-if="disabled">
              {{ data.item.hs_code }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="HS Code"
                rules="required"
              >
                <b-form-input
                  id="hs-code"
                  v-model="data.item.hs_code"
                  v-b-tooltip.hover.right="issueDescription(getUnifiedIssues(data.item), 'hs_code')"
                  :class="issueClass(getUnifiedIssues(data.item), 'hs_code')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(quantity)="data">
            <div v-if="disabled">
              {{ data.item.quantity }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Quantity"
                :rules="'required|minvalue:1'"
              >
                <b-form-input
                  id="quantity"
                  v-model="data.item.quantity"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'quantity')"
                  type="number"
                  size="sm"
                  :class="issueClass(getUnifiedIssues(data.item), 'quantity')"
                  :state="errors.length > 0 ? false:null"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(value)="data">
            <div v-if="disabled">
              {{ data.item.value }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Value"
                rules="required"
              >
                <b-form-input
                  id="value"
                  v-model="data.item.value"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'value')"
                  :class="issueClass(getUnifiedIssues(data.item), 'value')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(value_currency)="data">
            <div v-if="disabled">
              {{ data.item.value_currency }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Currency"
                rules="required|alpha|length:3"
              >
                <b-form-input
                  id="value-currency"
                  v-model="data.item.value_currency"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'value_currency')"
                  size="sm"
                  :class="issueClass(getUnifiedIssues(data.item), 'value_currency')"
                  :state="errors.length > 0 ? false:null"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(gross_weight)="data">
            <div v-if="disabled">
              {{ data.item.gross_weight }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Gross weight"
                :rules="parcel.parcel_gross_weight ? '' : 'required'"
              >
                <b-form-input
                  id="gross-weight"
                  v-model="data.item.gross_weight"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'gross_weight')"
                  :class="issueClass(getUnifiedIssues(data.item), 'gross_weight')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(manufacturer_standard_product_identifier)="data">
            <div v-if="disabled">
              {{ getProductIdentifier(data.item, 'C127') }}
            </div>
            <div v-else>
              <b-form-input
                :value="getProductIdentifier(data.item, 'C127')"
                maxlength="70"
                size="sm"
                @input="setProductIdentifier(data.item, 'C127', $event)"
              />
            </div>
          </template>

          <template #cell(manufacturer_non_standard_product_identifier)="data">
            <div v-if="disabled">
              {{ getProductIdentifier(data.item, 'C128') }}
            </div>
            <div v-else>
              <b-form-input
                :value="getProductIdentifier(data.item, 'C128')"
                maxlength="70"
                size="sm"
                @input="setProductIdentifier(data.item, 'C128', $event)"
              />
            </div>
          </template>

          <template #cell(merchant_product_identifier)="data">
            <div v-if="disabled">
              {{ getProductIdentifier(data.item, 'C129') }}
            </div>
            <div v-else>
              <b-form-input
                :value="getProductIdentifier(data.item, 'C129')"
                maxlength="70"
                size="sm"
                @input="setProductIdentifier(data.item, 'C129', $event)"
              />
            </div>
          </template>

          <template #cell(net_weight)="data">
            <div v-if="disabled">
              {{ data.item.net_weight }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Net weight"
                rules="required"
              >
                <b-form-input
                  id="net-weight"
                  v-model="data.item.net_weight"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'net_weight')"
                  :class="issueClass(getUnifiedIssues(data.item), 'net_weight')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(supplementary_unit)="data">
            <div v-if="disabled">
              {{ data.item.supplementary_unit }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Supplementary Unit"
              >
                <b-form-input
                  id="supplementary-unit"
                  v-model="data.item.supplementary_unit"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'supplementary_unit')"
                  :class="issueClass(getUnifiedIssues(data.item), 'supplementary_unit')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(origin_country)="data">
            <div v-if="disabled">
              {{ data.item.origin_country }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Country of origin"
              >
                <b-form-input
                  id="origin-country"
                  v-model="data.item.origin_country"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'origin_country')"
                  :class="issueClass(getUnifiedIssues(data.item), 'origin_country')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(supplementary_unit_value)="data">
            <div v-if="disabled">
              {{ data.item.supplementary_unit_value }}
            </div>
            <div v-else>
              <validation-provider
                #default="{ errors }"
                name="Supplementary Unit Value"
              >
                <b-form-input
                  id="supplementary-unit-value"
                  v-model="data.item.supplementary_unit_value"
                  v-b-tooltip.hover.top="issueDescription(getUnifiedIssues(data.item), 'supplementary_unit_value')"
                  :class="issueClass(getUnifiedIssues(data.item), 'supplementary_unit_value')"
                  :state="errors.length > 0 ? false:null"
                  size="sm"
                />
              </validation-provider>
            </div>
          </template>

          <template #cell(action)="data">
            <b-button
              variant="outline-danger"
              size="sm"
              @click="deleteItem(data.item)"
            >
              <feather-icon
                icon="TrashIcon"
                class="d-inline"
              />
            </b-button>
          </template>
        </b-table>
      </b-overlay>
    </b-row>
    <b-row>
      <!-- Errors -->
      <b-col
        v-if="error"
        lg="1"
      >
        <label>Errors</label>
        <b-alert
          show
          variant="danger"
        >
          {{ error.message }}
        </b-alert>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BFormInput,
  BButton,
  BOverlay,
  BTable,
  BAlert,
  BSpinner,
  BTr,
  BTd,
  VBTooltip,
} from 'bootstrap-vue'
import { ValidationProvider } from 'vee-validate'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BButton,
    ValidationProvider,
    BCol,
    BRow,
    BFormInput,
    BOverlay,
    BTable,
    BAlert,
    BSpinner,
    BTr,
    BTd,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    parcel: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    declaration: {
      type: Object,
      default: () => {},
    },
    error: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    const pageSize = 50
    let fields = [
      { key: 'rowNumber', label: '#', thStyle: { width: '10px' } },
      { key: 'description', label: 'Description' },
      { key: 'hs_code', label: 'HS Code', thStyle: { width: '85px' } },
      { key: 'quantity', label: 'Quantity', thStyle: { width: '85px' } },
      { key: 'value', label: 'Value', thStyle: { width: '85px' } },
      { key: 'value_currency', label: 'Currency', thStyle: { width: '85px' } },
      { key: 'gross_weight', label: 'Gross weight, kg', thStyle: { width: '100px' } },
    ]
    if (this.parcel.declaration_type === 'H7') {
      fields.push({ key: 'manufacturer_standard_product_identifier', label: 'Std. product ID', thStyle: { width: '100px' } })
      fields.push({ key: 'manufacturer_non_standard_product_identifier', label: 'Non-std. product ID', thStyle: { width: '100px' } })
      fields.push({ key: 'merchant_product_identifier', label: 'Merchant product ID', thStyle: { width: '100px' } })
    } else if (this.parcel.declaration_type === 'H1') {
      fields.push({ key: 'origin_country', label: 'Country of origin' })
      fields.push({ key: 'net_weight', label: 'Net weight' })
    }
    if (
      this.parcel.shipment?.entry_country === 'LV'
      || this.declaration?.customs_country === 'LV'
    ) {
      fields = fields.filter(field => field.key !== 'quantity')
    }
    return {
      fields: fields,
      pageSize,
      currentPage: 1,
      serverTotalItems: null,
      serverTotalPages: null,
      isLoadingItems: false,
      pageItems: null,
      originalPageItems: [],
      newItemCounter: 0,
    }
  },
  computed: {
    totalItems() {
      return this.serverTotalItems !== null ? this.serverTotalItems : (this.parcel.items || []).length
    },
    displayItems() {
      return this.pageItems !== null ? this.pageItems : (this.parcel.items || [])
    },
    sortedItems() {
      const items = [...this.displayItems]
      return items.sort((a, b) => {
        const aHasIssues = (a.issues && a.issues.length > 0) || (this.parcelItemIssues[a.id] && this.parcelItemIssues[a.id].length > 0)
        const bHasIssues = (b.issues && b.issues.length > 0) || (this.parcelItemIssues[b.id] && this.parcelItemIssues[b.id].length > 0)
        if (aHasIssues && !bHasIssues) return -1
        if (!aHasIssues && bHasIssues) return 1
        return 0
      })
    },
    itemsWithIssuesCount() {
      if (this.parcel.items_with_issues_count != null) {
        return this.parcel.items_with_issues_count
      }
      return this.displayItems.filter(
        item => (item.issues && item.issues.length > 0) || (this.parcelItemIssues[item.id] && this.parcelItemIssues[item.id].length > 0),
      ).length
    },
    totalPages() {
      return this.serverTotalPages !== null ? this.serverTotalPages : Math.ceil(this.totalItems / this.pageSize)
    },
    paginatedStartIndex() {
      return (this.currentPage - 1) * this.pageSize
    },
    paginatedItems() {
      if (this.serverTotalItems !== null) {
        // Server-side pagination: pageItems holds only the current page
        return this.sortedItems
      }
      const start = this.paginatedStartIndex
      return this.sortedItems.slice(start, start + this.pageSize)
    },
    pageTotalRows() {
      const totals = this.paginatedItems.reduce((result, item) => {
        const quantity = this.parseDecimalValue(item.quantity)
        const grossWeight = this.parseDecimalValue(item.gross_weight)
        const currency = String(item.value_currency || '').trim().toUpperCase()
        const value = this.parseDecimalValue(item.value)

        if (quantity !== null) {
          result.quantity += quantity
        }
        if (grossWeight !== null) {
          result.grossWeight += grossWeight
        }
        if (currency && value !== null) {
          result.values[currency] = (result.values[currency] || 0) + value
        }

        return result
      }, { quantity: 0, grossWeight: 0, values: {} })

      const currencies = Object.keys(totals.values).sort()
      const rows = currencies.length > 0 ? currencies : ['']

      return rows.map((currency, index) => ({
        description: index === 0 ? 'Page total' : '',
        quantity: index === 0 ? Number(totals.quantity.toFixed(3)).toString() : '',
        value: currency ? totals.values[currency].toFixed(2) : '',
        value_currency: currency,
        gross_weight: index === 0 ? totals.grossWeight.toFixed(3) : '',
      }))
    },
    tableFields() {
      const optionalHeader = []

      if (
        this.parcel.shipment?.entry_country === 'RO'
        || this.declaration?.customs_country === 'RO'
      ) {
        optionalHeader.push({ key: 'supplementary_unit', label: 'Supplementary Unit' })
        optionalHeader.push({ key: 'supplementary_unit_value', label: 'Supplementary Unit Value' })
      }

      const actionHeader = !this.disabled && this.totalItems > 1 ? [{ key: 'action', label: '', thStyle: { width: '50px' } }] : []

      return [...this.fields, ...optionalHeader, ...actionHeader]
    },
    parcelItemIssues() {
      if (!this.displayItems.length) return {}

      const errors = this.declaration?.status_info?.errors ?? []
      const fieldKeys = this.fields.map(f => f.key)

      return this.displayItems.reduce((acc, item) => {
        const issues = errors
          .filter(error => item?.id != null
            && error?.item_reference != null
            && String(error.item_reference) === String(item.id))
          .map(error => {
            const matchedField = error?.field ? fieldKeys.find(key => error.field.endsWith(key)) : null
            if (!matchedField) return null
            return {
              field: matchedField,
              type: 'warning',
              message: error?.message || '',
            }
          })
          .filter(Boolean)

        if (issues.length) {
          acc[item.id] = issues
        }
        return acc
      }, {})
    },
  },
  watch: {
    'parcel.items.length'() {
      if (this.serverTotalItems !== null) return
      if (this.currentPage > this.totalPages) {
        this.currentPage = Math.max(1, this.totalPages)
      }
    },
  },
  mounted() {
    if (this.parcel.id && this.parcel.shipment_id) {
      this.fetchItemsFromServer(1)
    }
  },
  methods: {
    parseDecimalValue(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      const parsed = Number(String(value).trim().replace(/\s/g, '').replace(',', '.'))
      return Number.isFinite(parsed) ? parsed : null
    },
    fetchItemsFromServer(page) {
      this.isLoadingItems = true
      const shipmentId = this.parcel.shipment_id
      const goodsShipmentId = this.parcel.id
      this.$http.get(`/v1/shipments/${shipmentId}/goods-shipments/${goodsShipmentId}/goods-items`, {
        params: { page, per_page: this.pageSize, with_issues_first: 1 },
      }).then(response => {
        this.pageItems = response.data.data
        this.originalPageItems = JSON.parse(JSON.stringify(response.data.data))
        this.serverTotalItems = response.data.meta.total
        this.serverTotalPages = response.data.meta.last_page
        this.currentPage = response.data.meta.current_page
      }).catch(() => {
        // Fall back to embedded items on error (e.g. endpoint not available)
      }).finally(() => {
        this.isLoadingItems = false
      })
    },
    usesServerPagination() {
      return this.serverTotalItems !== null
    },
    addRow() {
      this.newItemCounter += 1
      const newItem = {
        new_item: `new_${this.newItemCounter}`,
        item_number: '',
        description: '',
        quantity: '',
        hs_code: '',
        value: '',
        value_currency: '',
        gross_weight: '',
        supporting_documents: [],
      }
      if (this.usesServerPagination()) {
        this.pageItems.push(newItem)
        this.serverTotalItems += 1
      } else {
        this.parcel.items.push(newItem)
      }
    },
    getUnifiedIssues(item) {
      if (item.issues && item.issues.length > 0) {
        return item.issues
      }

      return this.parcelItemIssues[item.id] || []
    },
    deleteItem(item) {
      if (!item.id) {
        if (this.usesServerPagination()) {
          this.pageItems = this.pageItems.filter(x => x !== item)
          this.serverTotalItems = Math.max(0, this.serverTotalItems - 1)
        }
        if (this.parcel.items) {
          this.parcel.items = this.parcel.items.filter(x => x !== item)
        }
        return
      }

      this.isLoadingItems = true
      const shipmentId = this.parcel.shipment_id
      const goodsShipmentId = this.parcel.id
      this.$http.delete(`/v1/shipments/${shipmentId}/goods-shipments/${goodsShipmentId}/goods-items/${item.id}`).then(() => {
        if (this.usesServerPagination()) {
          this.pageItems = this.pageItems.filter(x => x.id !== item.id)
          this.originalPageItems = this.originalPageItems.filter(x => x.id !== item.id)
          this.serverTotalItems = Math.max(0, this.serverTotalItems - 1)
        }
        if (this.parcel.items) {
          this.parcel.items = this.parcel.items.filter(x => x.id !== item.id)
        }
        this.$emit('deleted', item.id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Parcel item removed',
            icon: 'EditIcon',
            variant: 'success',
          },
        })
      }).catch(error => {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || error.message || 'Failed to remove parcel item',
          },
        })
      }).finally(() => {
        this.isLoadingItems = false
      })
    },
    mapValidationErrors(errors) {
      const fieldKeys = this.fields.map(f => f.key)
      return Object.entries(errors).map(([field, messages]) => {
        const matched = fieldKeys.find(key => field.endsWith(key))
        return {
          field: matched || field,
          type: 'danger',
          message: Array.isArray(messages) ? messages[0] : messages,
        }
      })
    },
    saveItem(item) {
      const shipmentId = this.parcel.shipment_id
      const goodsShipmentId = this.parcel.id
      const base = `/v1/shipments/${shipmentId}/goods-shipments/${goodsShipmentId}/goods-items`
      // Clear stale validation issues before re-submitting
      this.$set(item, 'issues', [])
      // Strip UI-only keys before sending payload
      const {
        // eslint-disable-next-line no-unused-vars
        new_item: newItem, issues, ...payload
      } = item
      const request = item.id
        ? this.$http.put(`${base}/${item.id}`, payload)
        : this.$http.post(base, payload)
      return request.then(response => {
        if (!item.id) {
          const saved = response.data.data ?? response.data
          if (saved && saved.id) {
            this.$set(item, 'id', saved.id)
          }
          this.$delete(item, 'new_item')
        }
      }).catch(error => {
        const errors = error.response?.data?.errors
        if (errors) {
          this.$set(item, 'issues', this.mapValidationErrors(errors))
        }
        throw error
      })
    },
    // Persist only the current page's added/changed items (called by the parent on save)
    async saveCurrentPage() {
      if (!this.usesServerPagination()) {
        return
      }
      const requests = []
      this.pageItems.forEach(item => {
        if (!item.id) {
          requests.push(this.saveItem(item))
          return
        }
        const original = this.originalPageItems.find(o => o.id === item.id)
        if (!original || JSON.stringify(item) !== JSON.stringify(original)) {
          requests.push(this.saveItem(item))
        }
      })
      await Promise.all(requests)
      this.originalPageItems = JSON.parse(JSON.stringify(this.pageItems))
    },
    issueDescription(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].message
        }

        return ''
      }
      return ''
    },
    issueClass(issues, field) {
      if (issues?.length) {
        const index = issues.findIndex(item => item.field === field)

        if (index !== -1) {
          return issues[index].type === 'warning' ? 'input-warning' : 'input-danger'
        }

        return ''
      }
      return ''
    },
    getProductIdentifier(item, docType) {
      const doc = (item.supporting_documents || []).find(d => d.document_type === docType)
      return doc?.reference_number || ''
    },
    setProductIdentifier(item, docType, value) {
      if (!item.supporting_documents) {
        this.$set(item, 'supporting_documents', [])
      }
      const existing = item.supporting_documents.find(d => d.document_type === docType)
      if (existing) {
        if (value) {
          this.$set(existing, 'reference_number', value)
        } else {
          item.supporting_documents = item.supporting_documents.filter(d => d.document_type !== docType)
        }
      } else if (value) {
        item.supporting_documents.push({ document_type: docType, reference_number: value })
      }
    },
  },
}
</script>
<style scoped lang="scss">
@import '@core/scss/base/bootstrap-extended/variables';
.input-warning {
  background: lighten($warning, 35%);
  border: 1px solid $warning;
}
.input-danger {
  background: lighten($danger, 30%);
  border: 1px solid $danger;
}
</style>
